<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import dayjs from 'dayjs';
import { ToggleSwitch, Btn } from '@ds/components';
import {
	getMonthDays,
	createDaysForPreviousMonth,
	createDaysForNextMonth,
	normalizeDate,
} from './calendarUtils';

/**
 * Components uses dayjs library for formatting Strings to Dates. Safari and Firefox
 * can not process all date formats like Chrome and Edge so 'YYYY/MM/DD' is used as
 * the most compatible one.
 */

const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

/**
 * Disabled dates, if one on them is null the other covers all dates from or up to that
 * date respectively. (if { from: Date, to: null } then all dates from disabledDates from
 * will be disabled)
 * @property {Object} disabledDates -- Can be passed as a prop to disable certain dates.
 * @example
 * disabledDates: {
 *     from: Date,
 *     to: Date
 * }
 * @property {String} predefinedStart -- Could be passed to show a preselected start day
 * @property {String} predefinedEnd -- Could be passed to show a preselected end day
 */

type Day = { date: string; dayOfMonth: number; isCurrentMonth: boolean };
type DisabledDates = { from: string; to: string };

const emit = defineEmits([
	'startDaySelected',
	'endDaySelected',
	'clearDates',
	'toggleHasRange',
	'applyUserSelection',
]);

const props = defineProps<{
	hasRange?: boolean;
	disabledDates?: DisabledDates;
	predefinedStart?: string;
	predefinedEnd?: string;
	isShowingControls?: boolean;
	isShowingRangeToggle?: boolean;
}>();

const monthDays = ref();
const previousMonthDays = ref<Day[]>([]);
const nextMonthDays = ref<Day[]>([]);
const calendarDays = ref<Day[]>([]);
const today = ref();
const selectedMonth = ref();
const selectedStartDay = ref<string | null>(null);
const selectedEndDay = ref<string | null>(null);

const computedSelectedMonth = computed(() => {
	return dayjs(selectedMonth.value).format('MMMM YYYY');
});

watch(monthDays, () => {
	calendarDays.value = [...previousMonthDays.value, ...monthDays.value, ...nextMonthDays.value];
});

onMounted(() => {
	/**
	 * Component initializes with translated titles for weekdays.
	 * Current month, year and day. Selected start and end days are
	 * also set if provided from props.
	 *
	 * The arrays of days from current month
	 * with visible days from previous and next month
	 * are destructured in:
	 * @property {Array} calendarDays -- all days to be displayed.
	 *
	 * @function isDateDisabled -- Checks if date falls in the period of disabledDates
	 * @param {Date} date
	 *
	 * NAVIGATION:
	 * @function createCalendar -- re-initiates the calculation of days
	 * @function getMonth -- sets selectedMonth
	 * @param {String} type -- if next then selectedMonth = selectedMonth + 1 otherwise selectedMonth - 1
	 * and calls createCalendar function.
	 *
	 * @function onSelectDay -- first click set selectedStartDay, subsequent clicks set selectedEndDay
	 * @property {Date} selectedStartDay
	 * @property {Date} selectedEndDay
	 * @function clearDates -- sets selectedStartDay & selectedEndDay to null
	 * and emits custom event 'clearDates'
	 */

	// Init calendar with current month and year, unless startDate is passed:
	const initYear = props.predefinedStart
		? dayjs(props.predefinedStart).format('YYYY')
		: dayjs().format('YYYY');

	const initMonth = props.predefinedStart
		? dayjs(props.predefinedStart).format('M')
		: dayjs().format('M');

	today.value = dayjs().format('YYYY/MM/DD');

	// Get calendar visible days
	monthDays.value = getMonthDays(initYear, initMonth);
	previousMonthDays.value = createDaysForPreviousMonth(initYear, initMonth, monthDays.value);
	nextMonthDays.value = createDaysForNextMonth(initYear, initMonth, monthDays.value);
	calendarDays.value = [...previousMonthDays.value, ...monthDays.value, ...nextMonthDays.value];

	// Set selected
	selectedMonth.value = dayjs(`${initYear}/${initMonth}`);
	selectedStartDay.value = props.predefinedStart || null;
	selectedEndDay.value = props.predefinedEnd || null;
});

const createCalendar = (year: string, month: string) => {
	selectedMonth.value = dayjs(`${year}/${month}/1`);
	monthDays.value = getMonthDays(year, month);
	previousMonthDays.value = createDaysForPreviousMonth(year, month, monthDays.value);
	nextMonthDays.value = createDaysForNextMonth(year, month, monthDays.value);
};

const getMonth = (type: string) => {
	selectedMonth.value =
		type === 'next'
			? dayjs(selectedMonth.value).add(1, 'month')
			: dayjs(selectedMonth.value).subtract(1, 'month');

	createCalendar(selectedMonth.value.format('YYYY'), selectedMonth.value.format('M'));
};

const isDateDisabled = (date: string) => {
	const normalizeDateFrom = normalizeDate(props.disabledDates?.from);
	const normalizeDateTo = normalizeDate(props.disabledDates?.to);

	const hasBothLimits = props.disabledDates?.from && props.disabledDates?.to;

	const hasFromLimitAndIsLarger =
		!props.disabledDates?.to && props.disabledDates?.from && date >= normalizeDateFrom;

	const hasToLimitAndIsSmaller =
		props.disabledDates?.to && !props.disabledDates?.from && date < normalizeDateTo;

	const hasBothLimitsAndIsBetween =
		hasBothLimits && date <= normalizeDateTo && date >= normalizeDateFrom;

	return hasFromLimitAndIsLarger || hasToLimitAndIsSmaller || hasBothLimitsAndIsBetween;
};

const getDayClass = (day: Day) => {
	return [
		{ 'days-grid__calendar-day--disabled': isDateDisabled(day.date) },
		{ 'days-grid__calendar-day--not-current': !day.isCurrentMonth },
	];
};

const getPeriodClass = (day: Day) => {
	const normalizedStartDay = normalizeDate(selectedStartDay.value);
	const normalizedEndDay = normalizeDate(selectedEndDay.value);

	const isBetweenStartAndEnd =
		selectedEndDay.value && day.date < normalizedEndDay && day.date > normalizedStartDay;

	const isStartDay = day.date === normalizedStartDay;
	const isEndDay = day.date === normalizedEndDay;
	const isDayToday = day.date === today.value && day.isCurrentMonth;

	return [
		{ 'days-grid__calendar-day--period': isBetweenStartAndEnd },
		{ 'days-grid__calendar-day--selected': isStartDay },
		{ 'days-grid__calendar-day--selected days-grid__calendar-day--selected--end': isEndDay },
		{ 'days-grid__calendar-day--today': isDayToday },
	];
};

const onSelectDay = (day: Day) => {
	const selectedDay = day.dayOfMonth;
	const currentMonth = dayjs(day.date).format('MM');
	const currentYear = dayjs(day.date).format('YYYY');
	const selectedDate = normalizeDate(`${currentYear}/${currentMonth}/${selectedDay}`);

	if (selectedStartDay.value && !isDateDisabled(selectedDate)) {
		if (dayjs(selectedDate) <= dayjs(selectedStartDay.value) || !props.hasRange) {
			/** if second selection is an earlier date than the first,
			 * or is-single-date true then
			 * reset dates and set startDate again */
			selectedEndDay.value = null;
			emit('clearDates');

			selectedStartDay.value = selectedDate;
			emit('startDaySelected', selectedStartDay.value);
		} else {
			/** Otherwise emit the selected end day */
			selectedEndDay.value = selectedDate;
			emit('endDaySelected', selectedEndDay.value);
		}
	} else if (!selectedStartDay.value && !isDateDisabled(selectedDate)) {
		selectedStartDay.value = selectedDate;
		emit('startDaySelected', selectedStartDay.value);
	}
};

const clearUserSelection = () => {
	selectedStartDay.value = null;
	selectedEndDay.value = null;
};

const toggleHasRange = (hasRange: boolean) => {
	clearUserSelection();
	emit('toggleHasRange', hasRange);
};
</script>

<template>
	<div class="calendar" data-ts="app-calendar">
		<!-- The calendar header -->
		<section class="calendar__header">
			<!-- Month navigation previous -->
			<button
				id="previous-month-selector"
				aria-label="Previous month"
				name="previousMonthBtn"
				class="calendar__header__btn"
				type="button"
				data-ts="previous-month-btn"
				@click="getMonth('previous')"
			>
				<span class="bx bx-chevron-left text-lg" />
			</button>
			<!-- Month name, styled MMMM, YYYY -->
			<div class="calendar__header__month" data-ts="calendar-month">
				{{ computedSelectedMonth }}
			</div>
			<!-- Month navigation next -->
			<button
				id="next-month-selector"
				aria-label="Next month"
				name="nextMonthBtn"
				class="calendar__header__btn"
				type="button"
				data-ts="next-month-btn"
				@click="getMonth('next')"
			>
				<span class="bx bx-chevron-right text-lg" />
			</button>
		</section>

		<!-- Calendar subheader, simply displays days of the week ('Mon', 'Tue', etc) -->
		<ol class="weekdays" role="none">
			<li v-for="(weekday, index) in WEEKDAYS" :key="index" class="weekdays__day" role="none">
				{{ weekday }}
			</li>
		</ol>

		<!-- Calendar grid, all days including disabled dates from prev and next month -->
		<ul class="days-grid" role="none">
			<li
				v-for="(day, index) in calendarDays"
				:key="`day-btn-${index}`"
				:class="[getDayClass(day), getPeriodClass(day)]"
				class="days-grid__calendar-day"
				tabindex="0"
				:data-ts="`day-btn-${index}`"
				@click="onSelectDay(day)"
				@keydown.enter.prevent="onSelectDay(day)"
			>
				<div class="days-grid__calendar-day__number">
					{{ day.dayOfMonth }}
				</div>
			</li>
		</ul>
		<div v-show="isShowingControls || isShowingRangeToggle" class="calendar__controls">
			<toggle-switch
				v-if="isShowingRangeToggle"
				style="flex-grow: 2"
				label="Single date"
				label-right
				data-ts="rangeToggle"
				:is-checked="!hasRange"
				@change="toggleHasRange"
			/>
			<template v-if="isShowingControls">
				<Btn
					label="Clear"
					size="small"
					variant="secondary"
					class="calendar__btn mr-2"
					v-show="hasRange ? selectedStartDay && selectedEndDay : selectedStartDay"
					@click="clearUserSelection"
				/>
				<Btn
					label="Apply"
					size="small"
					class="calendar__btn"
					:disabled="hasRange ? !selectedStartDay || !selectedEndDay : !selectedStartDay"
					@click="$emit('applyUserSelection')"
					data-ts="date-picker-apply"
				/>
			</template>
		</div>
	</div>
</template>

<style lang="scss" scoped>
$app-calendar-day-size: 34px;
$app-calendar-width: 360px;

$app-calendar-color-selected-day: #fff;
$app-calendar-color-period-bg: #f5f5f5;
$app-calendar-color-hover-day-border: #31373f;
$app-calendar-color-not-current-day: #676767;
$app-calendar-color-disabled-day: #b4b4b4;
$app-calendar-color-text-and-selected: #242a33;

$app-calendar-border-1-grey: 1px solid $app-calendar-color-hover-day-border;

.calendar {
	position: relative;
	width: $app-calendar-width;
	background-color: $color-white;
	border: $border-width-1 $border-style $color-neutral-200;
	border-radius: $border-radius-2;
	font-size: $font-size-sm;
	padding: $spacing-2 $spacing-2 0 $spacing-2;
	box-shadow: $box-shadow-dropdown;

	&__header {
		/* Month indicator and selectors positioning */
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: space-between;
		background-color: $color-white;
		padding: $spacing-2 4% 0 4%;

		&__btn {
			display: flex;
			align-items: center;
			border-radius: $border-radius-1;
			cursor: pointer;

			@media (hover: hover) {
				&:hover:not([disabled]) {
					background-color: $color-neutral-100;
				}
			}

			&:focus-visible {
				outline: $border-width-1 $border-style $color-neutral-400;
			}
		}

		&__month {
			color: $app-calendar-color-text-and-selected;
			font-size: $font-size;
			font-weight: $font-weight-medium;
			margin-bottom: $spacing-2;
		}
	}

	&__controls {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		padding: $spacing-2 0;
		margin-top: $spacing-1;
		border-top: $border-width-1 $border-style $color-neutral-200;
	}
}

.weekdays,
.days-grid {
	/* 7 equal columns for weekdays and days cells */
	width: 100%;
	display: grid;
	grid-template-columns: repeat(7, 1fr);
	row-gap: 1px;
	place-items: center;
	border-radius: 8px;
	overflow: hidden;
	list-style-type: none;
}

.weekdays {
	/* | Mon | Tue | Wed | Thu | Fri | Sat | Sun | */
	color: $app-calendar-color-text-and-selected;
	font-size: $font-size-sm;
	font-weight: $font-weight-medium;
	background-color: $color-white;
	padding-bottom: $spacing-1;
	padding-top: $spacing-2;

	&__day {
		/* Position the weekday label within the cell */
		text-align: center;
	}
}

.days-grid {
	height: 70%;

	&__calendar-day {
		position: relative;
		width: 85%;
		height: 85%;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: $font-size-sm;
		font-weight: $font-weight-medium;
		background-color: $color-white;
		color: $app-calendar-color-text-and-selected;
		padding: $spacing-1;
		cursor: pointer;

		&:focus {
			outline: none;
		}

		&:hover > &__number,
		&:focus-visible > &__number {
			border: $app-calendar-border-1-grey;
		}

		&--not-current {
			color: $app-calendar-color-not-current-day;
			font-weight: $font-weight;
			background: transparent;
		}

		&--disabled {
			color: $app-calendar-color-disabled-day;
			text-decoration: line-through;
			background: transparent;
			pointer-events: none;
		}

		&--today {
			font-weight: $font-weight-bold;
			text-decoration: underline;
			background: transparent;
		}

		&--period {
			background-color: $app-calendar-color-period-bg;
			border-radius: 0;
			position: relative;
		}

		&--selected {
			& > .days-grid__calendar-day__number {
				background-color: $app-calendar-color-text-and-selected;
				color: $app-calendar-color-selected-day;
				border: 1px solid white;
			}

			&--end {
				background: linear-gradient(90deg, $app-calendar-color-period-bg, white);
				border-radius: 0 50px 50px 0;
			}
		}

		&--period,
		&--selected--end {
			/*
        Creates a highlight around start day, was not placed on start day
        because it needs to be visible only if a period selection exists
      */
			&::before {
				content: '';
				position: absolute;
				width: 75%;
				height: 100%;
				left: -75%;
				background-color: $app-calendar-color-period-bg;
				border-radius: 50px 0 0 50px;
			}
		}

		&__number {
			height: $app-calendar-day-size;
			width: $app-calendar-day-size;
			border-radius: 50%;
			display: flex;
			justify-content: center;
			align-items: center;
			z-index: 2;
		}
	}
}
</style>
